<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'
import { useTranslation } from 'i18next-vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import WithLabel from '@/components/WithLabel.vue'
import MediaUpload from '@/components/ui/MediaUpload.vue'
import UrlInput from '@/components/ui/UrlInput.vue'
import {
    getLogoUploadMaxFileSizeInBytes,
    getUploadImageAcceptedMimeTypes,
} from '@/utils/media-helper.ts'
import {
    createInstitution,
    updateInstitution,
    type CreateFormatInstitutionData,
    type UpdateFormatInstitutionData,
} from '@/api/formatInstitutionApi'
import {
    PARTICIPATES_IN_SELECTION_FUNCTIONS,
    SELECTABLE_ENTRY_FUNCTIONS,
    type FormatInstitution,
    type StoredFormatInstitution,
} from '@/types/formatInstitution.ts'
import { FormatInstitutionSchema } from '@/validation/schemas/formatInstitution'
import { useFormValidation } from '@/composables/useFormValidation'
import InstitutionFunctionHelp from '@/components/forms/institutions/InstitutionFunctionHelp.vue'

const visible = defineModel<boolean>('visible', { required: true })

const props = defineProps<{
    formatId: string
    institution?: StoredFormatInstitution | null
}>()

const emit = defineEmits<{
    saved: []
}>()

const { t } = useTranslation()

const helpModalVisible = ref(false)
const formElement = ref<HTMLFormElement | null>(null)
const formId = `${useId()}-institution-entry-form`
const descriptionId = `${useId()}-description`
const nameInputId = `${useId()}-name`
const functionInputId = `${useId()}-function`
const urlInputId = `${useId()}-url`
const participatesInputId = `${useId()}-participates`
const nameErrorId = `${useId()}-name-error`
const functionErrorId = `${useId()}-function-error`
const logoErrorId = `${useId()}-logo-error`

const emptyValue = (): Partial<FormatInstitution> => ({
    name: '',
    function: undefined,
    url: '',
    logo: undefined,
    participatesInSelection: false,
})

const value = ref<Partial<FormatInstitution>>(emptyValue())
const saving = ref(false)

watch(visible, (isVisible) => {
    if (!isVisible) return
    value.value = props.institution ? { ...props.institution } : emptyValue()
})

const { validate, createFieldHelper } = useFormValidation(value, FormatInstitutionSchema, t)

const nameField = createFieldHelper('name')
const functionField = createFieldHelper('function')
const urlField = createFieldHelper('url')
const logoField = createFieldHelper('logo')

const functionOptions = computed(() =>
    SELECTABLE_ENTRY_FUNCTIONS.map((functionValue) => ({
        label: t(`ui.institutions.function.${functionValue}.label`),
        value: functionValue,
    }))
)

const showsParticipatesInSelection = computed(() =>
    value.value.function
        ? PARTICIPATES_IN_SELECTION_FUNCTIONS.includes(value.value.function)
        : false
)

const save = async () => {
    if (!validate()) {
        await nextTick()
        formElement.value
            ?.querySelector<HTMLElement>('[aria-invalid="true"]:not(input[type="file"])')
            ?.focus()
        return
    }

    saving.value = true
    try {
        if (props.institution) {
            const payload: UpdateFormatInstitutionData = {
                function: value.value.function!,
                name: value.value.name!,
                url: value.value.url,
                logo: value.value.logo!,
                participatesInSelection: showsParticipatesInSelection.value
                    ? value.value.participatesInSelection
                    : false,
            }
            await updateInstitution(props.institution.documentId, payload)
        } else {
            const payload: CreateFormatInstitutionData = {
                format: props.formatId,
                function: value.value.function!,
                name: value.value.name!,
                url: value.value.url,
                logo: value.value.logo!,
                participatesInSelection: showsParticipatesInSelection.value
                    ? value.value.participatesInSelection
                    : false,
            }
            await createInstitution(payload)
        }

        emit('saved')
        visible.value = false
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        :closable="!saving"
        :draggable="false"
        :dismissable-mask="!saving"
        :close-on-escape="!saving"
        class="w-modal-md"
        :header="
            props.institution
                ? t('ui.institutions.entryModal.editTitle')
                : t('ui.institutions.entryModal.addTitle')
        "
        :aria-describedby="descriptionId"
        :pt="{
            content: { class: 'px-10 pt-12 pb-0' },
            footer: { class: 'px-10 pt-12 pb-10' },
        }"
    >
        <form
            :id="formId"
            ref="formElement"
            data-testid="institution-entry-form"
            class="space-y-8"
            @submit.prevent="save"
        >
            <p :id="descriptionId" class="paragraph-sm text-neutral-600">
                {{ t('ui.institutions.entryModal.description') }}
            </p>

            <div class="space-y-6">
                <WithLabel
                    :label="t('ui.institutions.entryModal.nameLabel')"
                    :input-id="nameInputId"
                    no-padding
                >
                    <InputText
                        :id="nameInputId"
                        v-model="value.name"
                        fluid
                        autofocus
                        data-testid="institution-entry-modal-name-input"
                        :invalid="nameField.invalid.value"
                        :aria-invalid="nameField.invalid.value"
                        :aria-describedby="nameField.invalid.value ? nameErrorId : undefined"
                        @update:model-value="nameField.onUpdate()"
                    />
                    <small v-if="nameField.invalid.value" :id="nameErrorId" class="text-danger">
                        {{ nameField.error.value }}
                    </small>
                </WithLabel>

                <WithLabel
                    :label="t('ui.institutions.entryModal.functionLabel')"
                    :input-id="functionInputId"
                    no-padding
                >
                    <Select
                        v-model="value.function"
                        :input-id="functionInputId"
                        data-testid="institution-entry-modal-function-select"
                        :options="functionOptions"
                        option-label="label"
                        option-value="value"
                        fluid
                        :invalid="functionField.invalid.value"
                        :aria-invalid="functionField.invalid.value"
                        :pt="{
                            label: {
                                'aria-describedby': functionField.invalid.value
                                    ? functionErrorId
                                    : undefined,
                            },
                        }"
                        @update:model-value="functionField.onUpdate()"
                    />
                    <small
                        v-if="functionField.invalid.value"
                        :id="functionErrorId"
                        class="text-danger"
                    >
                        {{ functionField.error.value }}
                    </small>
                </WithLabel>

                <Button
                    type="button"
                    outlined
                    size="small"
                    icon="pi pi-info-circle"
                    :label="t('ui.institutions.list.functionHelpLink')"
                    data-testid="institution-entry-modal-function-help-button"
                    @click="helpModalVisible = true"
                />

                <WithLabel
                    :label="t('ui.institutions.entryModal.urlLabel')"
                    :input-id="urlInputId"
                    optional-indicator
                    no-padding
                >
                    <UrlInput
                        :id="urlInputId"
                        v-model="value.url"
                        data-testid="institution-entry-modal-url"
                        fluid
                        :error="urlField.error.value"
                        @update:model-value="urlField.onUpdate()"
                    />
                </WithLabel>

                <WithLabel :label="t('ui.institutions.entryModal.logoLabel')" no-padding>
                    <MediaUpload
                        v-model="value.logo"
                        :accept="getUploadImageAcceptedMimeTypes()"
                        :max-file-size="getLogoUploadMaxFileSizeInBytes()"
                        media-type="image"
                        i18n-key="ui.institutions.entryModal.logo"
                        :label="t('ui.institutions.entryModal.logoLabel')"
                        :aria-describedby="logoField.invalid.value ? logoErrorId : undefined"
                        :invalid="logoField.invalid.value"
                        test-id-prefix="institution-entry-logo"
                        :show-label="false"
                        store-full-object
                        @update:model-value="logoField.onUpdate()"
                    />
                    <small v-if="logoField.invalid.value" :id="logoErrorId" class="text-danger">
                        {{ logoField.error.value }}
                    </small>
                </WithLabel>

                <div v-if="showsParticipatesInSelection" class="flex items-start gap-3">
                    <Checkbox
                        v-model="value.participatesInSelection"
                        binary
                        :input-id="participatesInputId"
                        data-testid="institution-entry-modal-participates-selection-checkbox"
                    />
                    <label :for="participatesInputId" class="paragraph-sm cursor-pointer">
                        {{ t('ui.institutions.entryModal.participatesInSelectionLabel') }}
                        <span class="block mt-2 text-xs text-neutral-600">
                            {{ t('ui.institutions.entryModal.participatesInSelectionHelpText') }}
                        </span>
                    </label>
                </div>
            </div>
        </form>

        <template #footer>
            <div class="flex w-full justify-end gap-3">
                <Button
                    type="submit"
                    :form="formId"
                    :label="
                        props.institution
                            ? t('ui.institutions.entryModal.editTitle')
                            : t('ui.institutions.entryModal.submitLabel')
                    "
                    :loading="saving"
                    data-testid="institution-entry-modal-submit-button"
                />
                <Button
                    type="button"
                    outlined
                    :label="t('ui.common.cancel')"
                    :disabled="saving"
                    data-testid="institution-entry-modal-cancel-button"
                    @click="visible = false"
                />
            </div>
        </template>
    </Dialog>

    <InstitutionFunctionHelp v-model:visible="helpModalVisible" />
</template>
